/**
 * Outil « Banc d'optique » (Boîte à outils, physique-chimie 5e → Tle).
 *
 * Explorer, selon le niveau :
 * - Ombres (collège) : source ponctuelle, objet opaque, écran ; rayons qui frôlent les bords,
 *   ombre propre, ombre portée, taille par Thalès.
 * - Réfraction (2nde) : deux milieux (air, eau, verre, diamant), rayon incident, réfléchi,
 *   réfracté ; lois de Snell-Descartes ; réflexion totale.
 * - Lentille (2nde, 1re) : objet AB devant une lentille convergente ; les trois rayons
 *   particuliers, image réelle ou virtuelle, grandissement ; en 1re, relation de conjugaison.
 * - Lunette (Tle) : lunette afocale, faisceau issu d'un point d'un astre, grossissement.
 * Défi : cinq questions selon le niveau. Logique : `optics.ts`.
 */

import { useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, ParamSlider, Segmented } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { McqChallenge } from '../../ui/mcq-challenge';
import { RichText } from '../../ui/rich-text';
import { FIGURE_BG_CLASS, HaloText, VectorArrow, angleArcPath } from '../../ui/svg-parts';
import {
  MEDIA,
  cm,
  criticalAngle,
  deg,
  fr,
  lensImage,
  lensLines,
  makeQuiz,
  nOf,
  partsFor,
  quizKinds,
  refract,
  refractionLines,
  shadowHeight,
  shadowLines,
  sig,
  telescope,
  telescopeLines,
  type MediumId,
  type Part,
} from './optics';

const W = 340;
const INK = 'var(--color-ink-700)';
const MUTED = 'var(--color-ink-400)';
const LIGHT = '#f59e0b';
const IMG = 'var(--color-accent-600)';
const RAY = 'var(--color-error-500)';

function Lines({ lines }: { lines: string[] }) {
  return (
    <Note>
      {lines.map((l) => (
        <span key={l} className="mb-1 block last:mb-0" data-testid="op-law">
          <RichText>{l}</RichText>
        </span>
      ))}
    </Note>
  );
}

function Readout({ rows }: { rows: Array<[string, string]> }) {
  return (
    <table className="w-full text-sm" data-testid="op-readings">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-ink-100 border-b last:border-0">
            <th scope="row" className="text-ink-600 py-1.5 pr-2 text-left font-normal">
              <RichText>{k}</RichText>
            </th>
            <td className="text-ink-900 py-1.5 text-right font-semibold tabular-nums">
              {frenchSpaces(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ---------- ombres ---------- */

function ShadowView({ rank }: { rank: number | null }) {
  const [d1, setD1] = useState(30);
  const [d2, setD2] = useState(90);
  const [h, setH] = useState(4);
  const H = 210;
  const s = 3; // px par cm
  const x0 = 24;
  const y0 = H / 2;
  const X = (d: number) => x0 + d * s;
  const sh = shadowHeight({ d1, d2, h });
  const top = y0 - (sh / 2) * s;
  const bot = y0 + (sh / 2) * s;
  return (
    <div className="space-y-4">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
        role="img"
        aria-label="Source, objet et ombre portée"
        data-testid="op-shadow"
      >
        {/* cône de lumière et zone d'ombre */}
        <polygon
          points={`${X(0)},${y0} ${X(d2)},${y0 - 100} ${X(d2)},${y0 + 100}`}
          fill={LIGHT}
          opacity={0.12}
        />
        <polygon
          points={`${X(d1)},${y0 - (h / 2) * s} ${X(d2)},${top} ${X(d2)},${bot} ${X(d1)},${y0 + (h / 2) * s}`}
          fill="var(--color-ink-700)"
          opacity={0.18}
        />
        <line x1={X(0)} y1={y0} x2={X(d2)} y2={top} stroke={LIGHT} strokeWidth={1.5} />
        <line x1={X(0)} y1={y0} x2={X(d2)} y2={bot} stroke={LIGHT} strokeWidth={1.5} />
        {/* écran et ombre portée */}
        <line
          x1={X(d2)}
          y1={8}
          x2={X(d2)}
          y2={H - 8}
          stroke="var(--color-ink-300)"
          strokeWidth={6}
        />
        <line
          x1={X(d2)}
          y1={top}
          x2={X(d2)}
          y2={bot}
          stroke="var(--color-ink-900)"
          strokeWidth={6}
          data-testid="op-cast"
        />
        <HaloText x={X(d2) - 6} y={Math.max(14, top - 6)} textAnchor="end" fontSize={10} fill={INK}>
          ombre portée
        </HaloText>
        {/* objet : face éclairée et ombre propre */}
        <rect x={X(d1) - 4} y={y0 - (h / 2) * s} width={4} height={h * s} fill={LIGHT} />
        <rect x={X(d1)} y={y0 - (h / 2) * s} width={4} height={h * s} fill="var(--color-ink-800)" />
        <HaloText x={X(d1) + 8} y={y0 + (h / 2) * s + 14} fontSize={10} fill={INK}>
          ombre propre
        </HaloText>
        {/* source */}
        <circle cx={X(0)} cy={y0} r={7} fill={LIGHT} />
        <HaloText x={X(0)} y={y0 + 22} textAnchor="middle" fontSize={11} fill={INK}>
          lampe
        </HaloText>
      </svg>
      <ParamSlider
        name="Distance lampe – objet"
        ariaLabel="Distance lampe – objet"
        value={d1}
        min={20}
        max={60}
        step={5}
        onChange={setD1}
        format={(v) => frenchSpaces(cm(v))}
      />
      <ParamSlider
        name="Distance lampe – écran"
        ariaLabel="Distance lampe – écran"
        value={d2}
        min={65}
        max={100}
        step={5}
        onChange={setD2}
        format={(v) => frenchSpaces(cm(v))}
      />
      <ParamSlider
        name="Hauteur de l’objet"
        ariaLabel="Hauteur de l’objet"
        value={h}
        min={2}
        max={8}
        step={1}
        onChange={setH}
        format={(v) => frenchSpaces(cm(v))}
      />
      <Readout
        rows={[
          ['Objet', cm(h)],
          ['Ombre portée', cm(sh)],
        ]}
      />
      <Lines lines={shadowLines({ d1, d2, h }, rank)} />
    </div>
  );
}

/* ---------- réfraction ---------- */

function RefractionView() {
  const [m1, setM1] = useState<MediumId>('air');
  const [m2, setM2] = useState<MediumId>('water');
  const [i1, setI1] = useState(45);
  const n1 = nOf(m1);
  const n2 = nOf(m2);
  const i2 = refract(n1, n2, i1);
  const lim = criticalAngle(n1, n2);
  const H = 240;
  const cx = W / 2;
  const cy = H / 2;
  const L = 105;
  const r = (a: number) => (a * Math.PI) / 180;
  const inc = { x: cx - L * Math.sin(r(i1)), y: cy - L * Math.cos(r(i1)) };
  const refl = { x: cx + L * Math.sin(r(i1)), y: cy - L * Math.cos(r(i1)) };
  const refr = i2 === null ? null : { x: cx + L * Math.sin(r(i2)), y: cy + L * Math.cos(r(i2)) };
  const tint = (n: number) => `rgba(59, 130, 246, ${Math.min(0.35, (n - 1) * 0.25)})`;
  return (
    <div className="space-y-4">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
        role="img"
        aria-label="Réfraction et réflexion"
        data-testid="op-refraction"
      >
        <rect x={0} y={0} width={W} height={cy} fill={tint(n1)} />
        <rect x={0} y={cy} width={W} height={H - cy} fill={tint(n2)} />
        <line x1={0} y1={cy} x2={W} y2={cy} stroke="var(--color-ink-500)" strokeWidth={1.5} />
        <line x1={cx} y1={10} x2={cx} y2={H - 10} stroke={MUTED} strokeDasharray="4 4" />
        <text x={8} y={18} fontSize={11} fill={INK}>
          {frenchSpaces(`${MEDIA.find((m) => m.id === m1)!.label} (n₁ = ${fr(n1, 2)})`)}
        </text>
        <text x={8} y={H - 10} fontSize={11} fill={INK}>
          {frenchSpaces(`${MEDIA.find((m) => m.id === m2)!.label} (n₂ = ${fr(n2, 2)})`)}
        </text>
        <VectorArrow x1={inc.x} y1={inc.y} x2={cx} y2={cy} color={RAY} width={2.5} />
        <VectorArrow
          x1={cx}
          y1={cy}
          x2={refl.x}
          y2={refl.y}
          color={RAY}
          width={i2 === null ? 2.5 : 1.4}
        />
        {refr && <VectorArrow x1={cx} y1={cy} x2={refr.x} y2={refr.y} color={RAY} width={2.5} />}
        <path d={angleArcPath(cx, cy, cx, cy - 40, inc.x, inc.y, 34)} fill="none" stroke={INK} />
        <HaloText x={cx - 22} y={cy - 40} fontSize={11} fill={INK} textAnchor="end">
          {`i₁ = ${fr(i1, 0)}°`}
        </HaloText>
        {refr && i2 !== null && (
          <>
            <path
              d={angleArcPath(cx, cy, cx, cy + 40, refr.x, refr.y, 34)}
              fill="none"
              stroke={INK}
            />
            <HaloText x={cx + 10} y={cy + 52} fontSize={11} fill={INK}>
              {`i₂ = ${fr(i2, 1)}°`}
            </HaloText>
          </>
        )}
        {i2 === null && (
          <HaloText
            x={cx + 10}
            y={cy + 30}
            fontSize={12}
            fontWeight={700}
            fill={RAY}
            data-testid="op-tir"
          >
            réflexion totale
          </HaloText>
        )}
      </svg>
      <div className="space-y-1">
        <p className="text-ink-600 text-xs font-semibold">Milieu 1 (rayon incident)</p>
        <Segmented
          ariaLabel="Milieu 1"
          value={m1}
          options={MEDIA.map((m) => ({ value: m.id, label: m.label }))}
          onChange={setM1}
        />
      </div>
      <div className="space-y-1">
        <p className="text-ink-600 text-xs font-semibold">Milieu 2 (rayon réfracté)</p>
        <Segmented
          ariaLabel="Milieu 2"
          value={m2}
          options={MEDIA.map((m) => ({ value: m.id, label: m.label }))}
          onChange={setM2}
        />
      </div>
      <ParamSlider
        name="Angle d’incidence i₁"
        ariaLabel="Angle d’incidence"
        value={i1}
        min={0}
        max={89}
        step={1}
        onChange={setI1}
        format={(v) => `${v}°`}
      />
      <Readout
        rows={[
          ['Angle de réfraction i₂', i2 === null ? 'pas de rayon réfracté' : deg(i2)],
          ['Angle limite', lim === null ? '— (n₂ ≥ n₁)' : deg(lim)],
        ]}
      />
      <Lines lines={refractionLines(n1, n2, i1)} />
    </div>
  );
}

/* ---------- lentille ---------- */

function LensView({ rank }: { rank: number | null }) {
  const [OA, setOA] = useState(-25);
  const [f, setF] = useState(10);
  const [AB, setAB] = useState(2);
  const im = lensImage({ f, OA, AB });
  const H = 230;
  const s = 4; // px par cm, horizontalement
  const sy = 12; // px par cm, verticalement (échelles différentes, comme sur un schéma)
  const ox = W / 2;
  const oy = H / 2;
  const X = (x: number) => ox + x * s;
  const Y = (y: number) => oy - y * sy;
  const xmax = W / 2 / s; // bord droit (cm)
  const xmin = -xmax;
  const Bx = OA;
  const By = AB;
  // rayon 1 : parallèle à l'axe, puis passe par F'
  const r1 = { inX: Bx, inY: By, lensY: By, slope: -By / f };
  // rayon 2 : par O, non dévié
  const slope2 = By / Bx; // y = slope2 · x
  // rayon 3 : passe par F (x = −f), puis parallèle
  const lensY3 = Bx === -f ? null : (By * f) / (Bx + f); // y à la lentille : droite B → F
  const outY = (y0: number, slope: number, x: number) => y0 + slope * x;
  const virtual = im.kind === 'virtual';
  const img = im.kind === 'infinite' ? null : { x: im.OA, y: im.AB };
  return (
    <div className="space-y-4">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
        role="img"
        aria-label="Lentille convergente : objet et image"
        data-testid="op-lens"
      >
        <defs>
          <clipPath id="op-lens-clip">
            <rect x={0} y={0} width={W} height={H} />
          </clipPath>
        </defs>
        <line x1={0} y1={oy} x2={W} y2={oy} stroke="var(--color-ink-400)" />
        {/* lentille */}
        <line
          x1={ox}
          y1={14}
          x2={ox}
          y2={H - 14}
          stroke="var(--color-primary-500)"
          strokeWidth={3}
        />
        <polygon points={`${ox},10 ${ox - 6},20 ${ox + 6},20`} fill="var(--color-primary-500)" />
        <polygon
          points={`${ox},${H - 10} ${ox - 6},${H - 20} ${ox + 6},${H - 20}`}
          fill="var(--color-primary-500)"
        />
        {[
          [-f, 'F'],
          [f, 'F’'],
        ].map(([x, l]) => (
          <g key={l as string}>
            <line
              x1={X(x as number)}
              y1={oy - 4}
              x2={X(x as number)}
              y2={oy + 4}
              stroke={INK}
              strokeWidth={2}
            />
            <HaloText x={X(x as number)} y={oy + 16} textAnchor="middle" fontSize={11} fill={INK}>
              {l as string}
            </HaloText>
          </g>
        ))}
        <HaloText x={ox + 6} y={oy + 16} fontSize={11} fill={INK}>
          O
        </HaloText>
        <g clipPath="url(#op-lens-clip)" stroke={RAY} strokeWidth={1.5} fill="none">
          {/* rayon 1 */}
          <line x1={X(Bx)} y1={Y(By)} x2={X(0)} y2={Y(r1.lensY)} />
          <line x1={X(0)} y1={Y(r1.lensY)} x2={X(xmax)} y2={Y(outY(r1.lensY, r1.slope, xmax))} />
          {/* rayon 2 */}
          <line x1={X(Bx)} y1={Y(By)} x2={X(xmax)} y2={Y(slope2 * xmax)} />
          {/* rayon 3 */}
          {lensY3 !== null && Math.abs(lensY3) < 60 && (
            <>
              <line x1={X(Bx)} y1={Y(By)} x2={X(0)} y2={Y(lensY3)} />
              <line x1={X(0)} y1={Y(lensY3)} x2={X(xmax)} y2={Y(lensY3)} />
            </>
          )}
          {/* prolongements (image virtuelle) */}
          {virtual && img && (
            <g strokeDasharray="4 4" opacity={0.8}>
              <line
                x1={X(0)}
                y1={Y(r1.lensY)}
                x2={X(Math.max(img.x, xmin))}
                y2={Y(outY(r1.lensY, r1.slope, Math.max(img.x, xmin)))}
              />
              <line
                x1={X(Bx)}
                y1={Y(By)}
                x2={X(Math.max(img.x, xmin))}
                y2={Y(slope2 * Math.max(img.x, xmin))}
              />
              {lensY3 !== null && (
                <line x1={X(0)} y1={Y(lensY3)} x2={X(Math.max(img.x, xmin))} y2={Y(lensY3)} />
              )}
            </g>
          )}
        </g>
        {/* objet */}
        <VectorArrow x1={X(OA)} y1={oy} x2={X(OA)} y2={Y(AB)} color={INK} width={3} />
        <HaloText x={X(OA)} y={oy + 16} textAnchor="middle" fontSize={11} fill={INK}>
          A
        </HaloText>
        <HaloText x={X(OA) - 8} y={Y(AB) - 4} textAnchor="end" fontSize={11} fill={INK}>
          B
        </HaloText>
        {/* image */}
        {img && img.x > xmin && img.x < xmax && Math.abs(img.y) * sy < H / 2 - 4 && (
          <g data-testid="op-image">
            <line
              x1={X(img.x)}
              y1={oy}
              x2={X(img.x)}
              y2={Y(img.y)}
              stroke={IMG}
              strokeWidth={3}
              strokeDasharray={virtual ? '5 4' : undefined}
            />
            <circle cx={X(img.x)} cy={Y(img.y)} r={3.5} fill={IMG} />
            <HaloText
              x={X(img.x) + 6}
              y={Y(img.y) + (img.y < 0 ? 12 : -4)}
              fontSize={11}
              fill={IMG}
            >
              B’
            </HaloText>
          </g>
        )}
      </svg>
      <ParamSlider
        name="Position de l’objet OA"
        ariaLabel="Position de l’objet"
        value={OA}
        min={-40}
        max={-2}
        step={1}
        onChange={setOA}
        format={(v) => frenchSpaces(cm(v))}
      />
      <ParamSlider
        name="Distance focale f’"
        ariaLabel="Distance focale"
        value={f}
        min={4}
        max={15}
        step={1}
        onChange={setF}
        format={(v) => frenchSpaces(cm(v))}
      />
      <ParamSlider
        name="Taille de l’objet AB"
        ariaLabel="Taille de l’objet"
        value={AB}
        min={1}
        max={4}
        step={0.5}
        onChange={setAB}
        format={(v) => frenchSpaces(cm(v))}
      />
      <Readout
        rows={[
          ['OA', cm(OA)],
          ['OA’', im.kind === 'infinite' ? 'à l’infini' : cm(im.OA)],
          ['A’B’', im.kind === 'infinite' ? '—' : cm(im.AB)],
          ['Grandissement γ', im.kind === 'infinite' ? '—' : sig(im.gamma, 2)],
          [
            'Image',
            im.kind === 'real' ? 'réelle' : im.kind === 'virtual' ? 'virtuelle' : 'à l’infini',
          ],
        ]}
      />
      <Lines lines={lensLines({ f, OA, AB }, rank)} />
    </div>
  );
}

/* ---------- lunette ---------- */

function TelescopeView() {
  const [f1, setF1] = useState(900);
  const [f2, setF2] = useState(100);
  const [aDeg, setADeg] = useState(1.5);
  const alpha = (aDeg * Math.PI) / 180;
  const t = telescope({ f1, f2, alpha });
  const H = 210;
  const x1 = 64;
  const s = (W - x1 - 70) / (f1 + f2);
  const x2 = x1 + (f1 + f2) * s;
  const xF = x1 + f1 * s;
  const oy = H / 2;
  const B1y = oy + f1 * Math.tan(alpha) * s; // image intermédiaire sous l'axe
  const half = 40;
  const heights = [-half, 0, half];
  const outSlope = (B1y - oy) / (x2 - xF); // pente (px/px) des rayons émergents, vers le haut
  return (
    <div className="space-y-4">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
        role="img"
        aria-label="Lunette afocale : marche d’un faisceau"
        data-testid="op-telescope"
      >
        <line x1={0} y1={oy} x2={W} y2={oy} stroke="var(--color-ink-400)" />
        <line
          x1={x1}
          y1={oy - 60}
          x2={x1}
          y2={oy + 60}
          stroke="var(--color-primary-500)"
          strokeWidth={3}
        />
        <line
          x1={x2}
          y1={oy - 30}
          x2={x2}
          y2={oy + 30}
          stroke="var(--color-primary-500)"
          strokeWidth={3}
        />
        <HaloText x={x1} y={oy - 66} textAnchor="middle" fontSize={10} fill={INK}>
          objectif
        </HaloText>
        <HaloText x={x2} y={oy - 36} textAnchor="middle" fontSize={10} fill={INK}>
          oculaire
        </HaloText>
        <line x1={xF} y1={oy - 4} x2={xF} y2={oy + 4} stroke={INK} strokeWidth={2} />
        <HaloText x={xF} y={oy - 8} textAnchor="middle" fontSize={10} fill={INK}>
          F’₁ = F₂
        </HaloText>
        <line x1={xF} y1={oy} x2={xF} y2={B1y} stroke={IMG} strokeWidth={2.5} />
        <HaloText x={xF + 4} y={B1y + 12} fontSize={10} fill={IMG}>
          B₁
        </HaloText>
        <g stroke={RAY} strokeWidth={1.4} fill="none">
          {heights.map((hgt) => {
            const yL1 = oy + hgt;
            const x0 = 0;
            const y0 = yL1 - (x1 - x0) * Math.tan(alpha); // arrive en descendant
            // après l'objectif : vers B₁ ; à l'oculaire :
            const k = (x2 - x1) / (xF - x1);
            const yL2 = yL1 + (B1y - yL1) * k;
            const yEnd = yL2 - outSlope * (W - x2);
            return (
              <g key={hgt}>
                <line x1={x0} y1={y0} x2={x1} y2={yL1} />
                <line x1={x1} y1={yL1} x2={x2} y2={yL2} />
                <line x1={x2} y1={yL2} x2={W} y2={yEnd} />
              </g>
            );
          })}
        </g>
        <line
          x1={xF}
          y1={B1y}
          x2={W}
          y2={B1y - (W - xF) * outSlope}
          stroke={MUTED}
          strokeDasharray="3 3"
        />
      </svg>
      <ParamSlider
        name="Focale de l’objectif f’₁"
        ariaLabel="Focale de l’objectif"
        value={f1}
        min={300}
        max={1200}
        step={100}
        onChange={setF1}
        format={(v) => `${fr(v, 0)} mm`}
      />
      <ParamSlider
        name="Focale de l’oculaire f’₂"
        ariaLabel="Focale de l’oculaire"
        value={f2}
        min={20}
        max={200}
        step={10}
        onChange={setF2}
        format={(v) => `${fr(v, 0)} mm`}
      />
      <ParamSlider
        name="Angle α (exagéré sur la figure)"
        ariaLabel="Angle alpha"
        value={aDeg}
        min={0.5}
        max={3}
        step={0.5}
        onChange={setADeg}
        format={(v) => frenchSpaces(`${fr(v, 1)}° = ${sig((v * Math.PI) / 180, 2)} rad`)}
      />
      <Readout
        rows={[
          ['Grossissement G = f’₁ ÷ f’₂', fr(t.G, 1)],
          ['α’ = G × α', `${sig(t.alphaP, 2)} rad`],
          ['A₁B₁ ≈ f’₁ × α', `${fr(t.A1B1, 1)} mm`],
          ['Longueur f’₁ + f’₂', `${fr(t.length, 0)} mm`],
        ]}
      />
      <Lines lines={telescopeLines({ f1, f2, alpha })} />
    </div>
  );
}

function Explore({ rank }: { rank: number | null }) {
  const parts = partsFor(rank);
  const [part, setPart] = useState<Part>(parts[0]!.id);
  return (
    <div className="space-y-4">
      {parts.length > 1 && (
        <Segmented
          ariaLabel="Partie"
          value={part}
          options={parts.map((p) => ({ value: p.id, label: p.label }))}
          onChange={setPart}
        />
      )}
      {part === 'shadow' && <ShadowView rank={rank} />}
      {part === 'refraction' && <RefractionView />}
      {part === 'lens' && <LensView rank={rank} />}
      {part === 'telescope' && <TelescopeView />}
    </div>
  );
}

export function OpticsTool({
  level = null,
  seed,
}: {
  /** niveau : parties et lois selon la classe (5e → Tle) */
  level?: string | null;
  /** graine du défi (tests) */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore rank={rank} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <McqChallenge kinds={quizKinds(rank)} make={makeQuiz} seed={seed} testId="op" rich />
      </div>
    </div>
  );
}
